import { Component } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DynamicDialogComponent } from 'src/app/components/shared/dynamic-dialog/dynamic-dialog.component';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { ConnectionService, HasuraRole } from 'src/app/services/connection.service';
import { ApiService } from 'src/app/services/api.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { SentryService } from 'src/app/services/sentry.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { UserService } from 'src/app/services/user.service';
import { ApolloService, getUser_users_by_pk, getUsers_users } from 'src/gql-generated/generated';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NgIf, NgClass } from '@angular/common';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatTooltip } from '@angular/material/tooltip';
import { CdkCopyToClipboard } from '@angular/cdk/clipboard';

/**
 * Custom validator to check whether an email address already exists in the logged in user's organization
 * @param getEmails function to extract email addresses
 * @returns error or null
 */
export function emailExistsSyncValidator(
    getEmails: () => string[],
): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }
        const exists = getEmails().includes(control.value.toLowerCase());

        return exists ? { emailTaken: true } : null;
    };
}

@Component({
    selector: 'app-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrl: './user-edit.component.scss',
    imports: [
        MatButton,
        RouterLink,
        MatIcon,
        NgIf,
        MatFormField,
        MatInput,
        ReactiveFormsModule,
        MatError,
        NgClass,
        MatRadioGroup,
        MatRadioButton,
        MatIconButton,
        MatTooltip,
        CdkCopyToClipboard,
        DynamicDialogComponent,
    ],
})
export class UserEditComponent {
    /**
     * Loaded user. Only available in editing mode
     */
    user?: getUser_users_by_pk;

    /**
     * Current user id. Only available in editing mode
     */
    userId = this.route.snapshot.paramMap.get('userId');

    /**
     * Formgroup tracking required fields of an user
     */
    userFormGroup = new FormGroup({
        firstName: new FormControl<string>('', [Validators.required, Validators.maxLength(100)]),
        lastName: new FormControl<string>('', [Validators.required, Validators.maxLength(100)]),
        email: new FormControl<string>('', [
            Validators.required,
            Validators.email,
            Validators.maxLength(100),
            emailExistsSyncValidator(() => this.usersInOrg.map((user) => user.email)),
        ]),
        role: new FormControl<HasuraRole | undefined>(this.userId ? undefined : 'chatter', Validators.required),
    });

    /**
     * Array of users currently existing in the logged in user's organization.
     */
    private usersInOrg: getUsers_users[] = [];

    /**
     * Temporary password
     */
    temporaryPassword = this.generateTemporaryPassword();

    /**
     * True when password needs to updated for an existing user
     */
    updatePassword = false;

    /**
     * True when awaiting an async operation. Used to prevent the user from (accidentally) running a function twice.
     */
    awaitingAsyncOperation = false;

    constructor(
        private apollo: ApolloService,
        private route: ActivatedRoute,
        private api: ApiService,
        private snackbar: SnackbarService,
        private router: Router,
        public organization: OrganizationService,
        public userService: UserService,
        public connection: ConnectionService,
        private sentry: SentryService,
        public breakpoint: BreakpointService,
    ) {
        this.loadUser();
    }

    /**
     * Load user data
     */
    loadUser(): void {
        // Load user if id is found in URL
        if (this.userId) {
            firstValueFrom(this.apollo.getUser({ id: this.userId })).then((data) => {
                if (data.data?.users_by_pk) {
                    // Store user and load values into userFormGroup
                    this.user = data.data.users_by_pk;
                    this.userFormGroup.patchValue({
                        firstName: this.user.first_name,
                        lastName: this.user.last_name,
                        email: this.user.email,
                        role: this.user.role as HasuraRole,
                    });
                    // Disable role selection if user is admin and we are not editing ourself
                    if (this.user.role === 'admin' && (this.user.id !== this.userService.profile.id)) {
                        this.userFormGroup.controls.role.disable();
                    }
                    // Only admins can edit other admins, redirect otherwise
                    if (this.user.role === 'admin' && this.userService.profile.role !== 'admin') {
                        this.router.navigate(['/']);
                    }
                }
            });

            // Disable email field
            this.userFormGroup.controls.email.disable();

            // Don't show password until user presses button
            this.updatePassword = false;
        } else {
            this.userFormGroup.reset({ role: 'chatter' });
            this.updatePassword = true;

            // if new user, load all existing users in organization to validate mailaddresses against
            this.loadExistingUsers();
        }
    }

    /**
     * Get all the users that are currently existing in the logged in user's organization to validate
     * new email addresses against.
     */
    private async loadExistingUsers(): Promise<void> {
        this.usersInOrg = (await firstValueFrom(this.apollo.getUsers({
            where: {
                deleted_at: { _is_null: true },
                organization_id: { _eq: this.organization.currentOrganization$.value!.id },
            },
        }))).data?.users || [];
    }

    /**
     * Attempt to save user
     */
    async saveUser(): Promise<void> {
        if (!this.awaitingAsyncOperation) {
            this.awaitingAsyncOperation = true;
            this.userFormGroup.markAllAsTouched();
            if (this.userFormGroup.valid) {
                try {
                    // Attempt user insert
                    const addUserToKeycloakResult = await this.api.addUserToKeycloak(
                        this.userFormGroup.value.firstName!,
                        this.userFormGroup.value.lastName!,
                        this.userFormGroup.value.email!,
                        this.userFormGroup.value.role!,
                        this.temporaryPassword,
                        this.organization.currentOrganization$.value!.id,
                    );

                    if (addUserToKeycloakResult.id) {
                        this.snackbar.success($localize`Gebruiker succesvol aangemaakt`);
                        // new temp password for possible next entry
                        this.temporaryPassword = this.generateTemporaryPassword();
                        this.router.navigate(['settings', 'users']);
                    } else {
                        this.snackbar.error($localize`Gebruiker aanmaken mislukt`);
                    }
                } catch (error: any) {
                    // If user already exists in keycloak, we should enable the keycloak account and update the hasura user.
                    // In the hasura update we should make sure deleted_at is set to null, and user has organization_id set to current
                    // organization.
                    if (error?.error === 'User exists with same username') {
                        try {
                            const updateResponse = await firstValueFrom(this.apollo.updateUsers({
                                where: { email: { _eq: this.userFormGroup.value.email } },
                                updates: {
                                    deleted_at: null,
                                    organization_id: this.organization.currentOrganization$.value?.id,
                                    created_at: 'now()',
                                },
                            }));
                            const keycloakUserId = updateResponse.data?.update_users?.returning[0].id;
                            if (keycloakUserId) {
                                await this.api.setUserKeycloakStatus(keycloakUserId, true);
                                this.snackbar.success($localize`Gebruiker succesvol aangemaakt`);
                                // new password for next possible entry
                                this.temporaryPassword = this.generateTemporaryPassword();
                                this.router.navigate(['settings', 'users']);
                            } else {
                                this.sentry.error(error);
                                this.snackbar.error($localize`Gebruiker aanmaken mislukt`);
                            }
                        } catch (err) {
                            this.sentry.error(err);
                            this.snackbar.error($localize`Gebruiker aanmaken mislukt`);
                        }
                    } else {
                        this.sentry.error(error);
                        this.snackbar.error($localize`Gebruiker aanmaken mislukt`);
                    }
                }
            }
            this.awaitingAsyncOperation = false;
        }
    }

    /**
     * Attempt to save user updates
     */
    async updateUser(): Promise<void> {
        if (!this.awaitingAsyncOperation) {
            this.awaitingAsyncOperation = true;
            this.userFormGroup.markAllAsTouched();
            if (this.userFormGroup.valid && this.userId) {
                try {
                    // Update user in keycloak
                    await this.api.updateKeycloakUser(
                        this.userId,
                        this.userFormGroup.value.firstName!,
                        this.userFormGroup.value.lastName!,
                        // Get raw value here as role control could be disabled if we're editing an admin
                        this.userFormGroup.getRawValue().role!,
                        this.updatePassword ? this.temporaryPassword : undefined,
                    );

                    // Update user in hasura
                    const updateResponse = await firstValueFrom(this.apollo.updateUser({
                        id: this.userId,
                        _set: {
                            first_name: this.userFormGroup.value.firstName,
                            last_name: this.userFormGroup.value.lastName,
                        },
                    }));

                    // Notify user if successful
                    if (updateResponse.data?.update_users_by_pk?.id) {
                        this.snackbar.success($localize`Gebruiker succesvol aangepast`);
                        // new temporary password for possible next entry
                        this.temporaryPassword = this.generateTemporaryPassword();
                        // Navigate back to users page
                        this.router.navigate(['settings', 'users']);
                    }
                } catch (error) {
                    this.sentry.error(error);
                    this.snackbar.error($localize`Gebruiker aanpassen mislukt`);
                }
            }
            this.awaitingAsyncOperation = false;
        }
    }

    /**
     * Attempts to soft-delete the user. Disables the user in keycloak
     * @param dialog Dialog to ask for user confirmation
     */
    async deleteUser(dialog: DynamicDialogComponent): Promise<void> {
        if (this.user) {
            // Await user confirmation
            const accepted = await dialog.open();
            if (accepted) {
                try {
                    // Disable user in keycloak
                    await this.api.setUserKeycloakStatus(this.user.id, false);

                    // Soft-delete user in hasura
                    const updateResponse = await firstValueFrom(this.apollo.updateUser({
                        id: this.user.id,
                        _set: { deleted_at: 'now()' },
                    }));

                    // Notify user if successful
                    if (updateResponse.data?.update_users_by_pk?.id) {
                        this.snackbar.success($localize`Gebruiker succesvol verwijderd`);
                        // Refresh data
                        this.router.navigate(['settings', 'users']);
                    }
                } catch (error) {
                    this.sentry.error(error);
                    this.snackbar.error($localize`Gebruiker verwijderen mislukt`);
                }
            }
        }
    }

    /**
     * Generate a pseudo-random password from non-amibguous characters
     * @param length Length of password
     * @returns Randomly generated password
     */
    generateTemporaryPassword(length: number = 12): string {
        const validCharacters = 'ABCDEFGHJKLMNPRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'.split('');
        let password = '';
        for (let i = 0; i < length; i++) {
            const randomIndex = Math.floor(Math.random() * validCharacters.length);
            password += validCharacters[randomIndex];
        }
        return password;
    }
}
